import Grid from "@material-ui/core/Grid";
import React from "react";
import { useTranslation } from "react-i18next";

import { Paper, TextField, Typography } from "@material-ui/core";
import { Done } from "@material-ui/icons";
import Build from "@material-ui/icons/Build";
import NotificationsIcon from "@material-ui/icons/Notifications";
import TimelineIcon from "@material-ui/icons/Timeline";
import BadgeLink from "../../Components/BadgeLink";
import Title from "../../Components/Title";
import {
  ECABIN,
  formatTimeWithTZOffset,
  getToken,
  getUser,
  TICKET_PRIORITIES,
  ticketListRowStyle,
  USER_GROUPS,
} from "../../Utils/Common";
import EnvSettings from "../../Utils/EnvSettings";
import { useStyles } from "../../Utils/Styles";

import { CircularProgress, Tooltip } from "@material-ui/core";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import AttachMoneyIcon from "@material-ui/icons/AttachMoney";
import { useEffect, useState } from "react";
import SortableTable from "../../Components/SortableTable";
import TicketEntry from "../../Components/TicketEntry";
import { getTickets } from "../../Utils/ApiCalls";

const server = EnvSettings.server;
const GMAIL_OAUTH_REQUIRED_MESSAGE =
  "Gmail OAuth credentials are not configured";
const GMAIL_TEST_RECIPIENT = "hseven1@gmail.com";
const GMAIL_TEST_SUBJECT = "Test";

const callGmailEndpoint = async (endpoint, callBody) => {
  const response = await fetch(server + endpoint, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      token: getToken(),
      ...(callBody || {}),
    }),
  });
  const result = await response.json().catch(() => ({
    error: true,
    message: "Gmail endpoint returned an invalid response",
  }));

  if (!response.ok && !result.error) {
    return {
      ...result,
      error: true,
      message: response.statusText || "Gmail request failed",
    };
  }
  return result;
};

const getGmailOAuthReturnUrl = () => {
  var queryParams = new URLSearchParams(window.location.search);
  var cleanedSearch;

  queryParams.delete("gmailOAuth");
  queryParams.delete("gmailOAuthMessage");
  cleanedSearch = queryParams.toString();

  return (
    window.location.origin +
    window.location.pathname +
    (cleanedSearch ? "?" + cleanedSearch : "") +
    window.location.hash
  );
};

const getGmailHeader = (gmailMessage, headerName) => {
  var headers = gmailMessage?.payload?.headers || [];
  var header = headers.find(
    (h) => h.name && h.name.toLowerCase() === String(headerName).toLowerCase(),
  );
  return header?.value;
};

const shouldStartGmailOAuth = (result) => {
  var message = result?.message || "";
  return (
    message === GMAIL_OAUTH_REQUIRED_MESSAGE ||
    /invalid_grant|refresh token|access token/i.test(message)
  );
};

const formatGmailTestResult = (gmailMessage, emailMessage) => {
  var subject =
    getGmailHeader(gmailMessage, "Subject") ||
    emailMessage?.subject ||
    "(no subject)";
  var sender =
    getGmailHeader(gmailMessage, "From") || emailMessage?.sender || "";
  var snippet = gmailMessage?.snippet || emailMessage?.snippet || "";

  return [
    "Fetched latest Gmail email:",
    '"' + subject + '"',
    sender ? "from " + sender : "",
    snippet ? "- " + snippet : "",
  ]
    .filter(Boolean)
    .join(" ");
};

export default function MaintenanceModule() {
  const { t } = useTranslation();
  const classes = useStyles();
  const [showTicketEntry, setShowTicketEntry] = useState(false);
  const [refreshList, setRefreshList] = useState("dsfsdfds");
  const [tickets, setTickets] = useState();
  const [selectedTicket, setSelectedTicket] = useState();
  const [filteredList, setFilteredList] = useState();
  const [searchVal, setSearchVal] = useState();
  const [gmailTestLoading, setGmailTestLoading] = useState(false);
  const [gmailSendLoading, setGmailSendLoading] = useState(false);
  const [gmailTestResult, setGmailTestResult] = useState();
  const isSuperUser = getUser().permissionlevel === USER_GROUPS.SUPER_USER;

  const badgeLinks = [
    {
      href: "/cpanel/maintenance/ticketarchive",
      title: t("maintenance.ticketArchive"),
      icon: Done,
    },
    {
      href: "/cpanel/maintenance/tickets",
      title: t("maintenance.activeTickets"),
      icon: Build,
    },
    {
      href: "/cpanel/maintenance/statistics",
      title: t("maintenance.statistics"),
      icon: TimelineIcon,
    },
    {
      href: "/cpanel/alerts",
      title: t("alerts.header"),
      icon: NotificationsIcon,
    },
  ];

  let handleRowClick = (data) => {
    if (data) {
      setSelectedTicket(data);
      setShowTicketEntry(true);
    }
  };

  const handleScrollClick = (event, id) => {};

  const handleGmailTestClick = async () => {
    setGmailTestLoading(true);
    setGmailTestResult("Fetching latest Gmail email...");

    try {
      var fetchResult = await callGmailEndpoint("/fetch_gmail_messages");

      if (!fetchResult.error) {
        if (fetchResult.messages?.length) {
          setGmailTestResult(
            formatGmailTestResult(
              fetchResult.messages[0],
              fetchResult.emailmessages?.[0],
            ),
          );
        } else {
          setGmailTestResult("No Gmail messages found.");
        }
        return;
      }

      if (!shouldStartGmailOAuth(fetchResult)) {
        setGmailTestResult(
          fetchResult.message || "Failed to fetch Gmail messages.",
        );
        return;
      }

      setGmailTestResult("Gmail authorization required. Redirecting...");
      var oauthResult = await callGmailEndpoint("/get_gmail_oauth_url", {
        returnUrl: getGmailOAuthReturnUrl(),
      });

      if (oauthResult.error || !oauthResult.oauthUrl) {
        setGmailTestResult(
          oauthResult.message || "Failed to create Google OAuth URL.",
        );
        return;
      }

      window.location.assign(oauthResult.oauthUrl);
    } catch (error) {
      setGmailTestResult(error.message || "Failed to run Gmail test.");
    } finally {
      setGmailTestLoading(false);
    }
  };

  const redirectToGmailOAuth = async () => {
    setGmailTestResult("Gmail authorization required. Redirecting...");
    var oauthResult = await callGmailEndpoint("/get_gmail_oauth_url", {
      returnUrl: getGmailOAuthReturnUrl(),
    });

    if (oauthResult.error || !oauthResult.oauthUrl) {
      setGmailTestResult(
        oauthResult.message || "Failed to create Google OAuth URL.",
      );
      return false;
    }

    window.location.assign(oauthResult.oauthUrl);
    return true;
  };

  const handleGmailSendTestClick = async () => {
    setGmailSendLoading(true);
    setGmailTestResult("Sending Gmail test email...");

    try {
      var sendResult = await callGmailEndpoint("/send_gmail_message", {
        recipient: GMAIL_TEST_RECIPIENT,
        subject: GMAIL_TEST_SUBJECT,
        bodyText: "This is a Gmail integration test email.",
      });

      if (!sendResult.error) {
        setGmailTestResult(
          'Sent Gmail test email "' +
            GMAIL_TEST_SUBJECT +
            '" to ' +
            GMAIL_TEST_RECIPIENT +
            ".",
        );
        return;
      }

      if (shouldStartGmailOAuth(sendResult)) {
        await redirectToGmailOAuth();
        return;
      }

      setGmailTestResult(
        sendResult.message || "Failed to send Gmail test email.",
      );
    } catch (error) {
      setGmailTestResult(error.message || "Failed to send Gmail test email.");
    } finally {
      setGmailSendLoading(false);
    }
  };

  useEffect(() => {
    var queryParams = new URLSearchParams(window.location.search);
    var gmailOAuthStatus = queryParams.get("gmailOAuth");
    var gmailOAuthMessage = queryParams.get("gmailOAuthMessage");
    var cleanedSearch;

    if (!gmailOAuthStatus) {
      return;
    }

    if (gmailOAuthStatus === "success") {
      setGmailTestResult(
        "Google OAuth connected. Click Test Gmail again to fetch the latest email.",
      );
    } else {
      setGmailTestResult(gmailOAuthMessage || "Google OAuth failed.");
    }

    queryParams.delete("gmailOAuth");
    queryParams.delete("gmailOAuthMessage");
    cleanedSearch = queryParams.toString();
    window.history.replaceState(
      {},
      document.title,
      window.location.pathname +
        (cleanedSearch ? "?" + cleanedSearch : "") +
        window.location.hash,
    );
  }, []);

  useEffect(() => {
    setTickets();
    getTickets(
      {
        AssigneeId: getUser().userId,
        states: ["new", "progress"],
      },
      (result) => {
        setTickets(result.tickets);
      },
    );
  }, [refreshList]);

  useEffect(() => {
    if (searchVal && tickets) {
      var arr = tickets.filter((ti) => {
        if (!ti) {
          return false;
        }
        if (searchVal) {
          return !!Object.keys(ti).find(
            (k) =>
              (ti[k] &&
                String(ti[k])
                  .toLowerCase()
                  .includes(searchVal.toLowerCase())) ||
              (typeof ti[k] === "object" &&
                JSON.stringify(ti[k])
                  .toLowerCase()
                  .includes(searchVal.toLowerCase())),
          );
        }
        return true;
      });

      setFilteredList(arr);
    } else {
      setFilteredList(tickets);
    }
  }, [searchVal, tickets]);

  return (
    <Grid container justify="center" spacing={1}>
      <TicketEntry
        showTicketEntry={showTicketEntry}
        setShowTicketEntry={setShowTicketEntry}
        selectedTicket={selectedTicket}
        setSelectedTicket={setSelectedTicket}
        showHeader={true}
        showSearch={true}
        setRefreshList={setRefreshList}
      />
      <Grid item xs={12}>
        <Paper className={classes.paper}>
          <Grid container spacing={1}>
            {/* All the admin items as badges */}
            {badgeLinks
              .sort((a, b) => {
                return a.title > b.title ? 1 : -1;
              })
              .map((b, i) =>
                b.superuser &&
                getUser().permissionlevel !== USER_GROUPS.SUPER_USER ? null : (
                  <BadgeLink
                    key={i}
                    href={b.href}
                    title={b.title}
                    icon={b.icon}
                  />
                ),
              )}
          </Grid>
        </Paper>
      </Grid>

      {/*isSuperUser ? (
        <Grid item xs={12}>
          <Paper className={classes.paper}>
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={12} md={8}>
                <Title>Gmail test</Title>
                <Typography variant="body2" color="textSecondary">
                  {gmailTestResult ||
                    "Fetch latest Gmail email, or start Google OAuth if authorization is missing."}
                </Typography>
              </Grid>
              <Grid
                item
                xs={12}
                md={4}
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 8,
                  justifyContent: "flex-end",
                }}
              >
                <Button
                  color="primary"
                  disabled={gmailTestLoading || gmailSendLoading}
                  onClick={handleGmailTestClick}
                  variant="contained"
                >
                  {gmailTestLoading ? (
                    <CircularProgress
                      color="inherit"
                      size={20}
                      style={{ marginRight: 8 }}
                    />
                  ) : (
                    <MailOutlineIcon
                      style={{ fontSize: 20, marginRight: 8 }}
                    />
                  )}
                  Test Gmail
                </Button>
                <Button
                  color="primary"
                  disabled={gmailTestLoading || gmailSendLoading}
                  onClick={handleGmailSendTestClick}
                  variant="contained"
                >
                  {gmailSendLoading ? (
                    <CircularProgress
                      color="inherit"
                      size={20}
                      style={{ marginRight: 8 }}
                    />
                  ) : (
                    <SendIcon style={{ fontSize: 20, marginRight: 8 }} />
                  )}
                  Send Test
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      ) : null */}

      <Grid item xs={12}>
        <Paper className={classes.paper}>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Title>{t("maintenance.header")}</Title>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                onChange={(e, v) => {
                  setSearchVal(e.target.value);
                }}
                variant="outlined"
                label={t("search")}
              />
            </Grid>
            <Grid item xs={12}>
              <Title>{t("ticketList.yourTickets")}</Title>
            </Grid>
            <Grid item xs={12}>
              {filteredList && !filteredList.length ? (
                <Typography>{t("ticketList.noActiveTickets")}</Typography>
              ) : filteredList ? (
                <SortableTable
                  tableLayout={"fixed"}
                  headCells={[
                    {
                      id: "attachments",
                      hideFromCsv: true,
                      width: 32,
                      label: "",
                    },
                    {
                      id: "expenses",
                      hideFromCsv: true,
                      width: 32,
                      label: "",
                    },
                    {
                      id: "priority",
                      numeric: false,
                      disablePadding: false,
                      label: t("ticketList.priority"),
                    },
                    {
                      id: "Device.serialNumber",
                      numeric: false,
                      disablePadding: false,
                      label: t("serialNumber"),
                    },
                    {
                      id: "description",
                      numeric: false,
                      disablePadding: false,
                      label: t("ticketList.description"),
                    },
                    {
                      id: "action",
                      numeric: false,
                      disablePadding: false,
                      label: t("ticketList.action"),
                    },
                    {
                      id: "details",
                      numeric: false,
                      disablePadding: false,
                      label: t("ticketList.details"),
                    },
                    {
                      id: "updatedAt",
                      numeric: false,
                      disablePadding: false,
                      label: t("updatedAt"),
                    },
                  ]}
                  getRowStyle={ticketListRowStyle}
                  defaultSortRow={"priority"}
                  handleRowClick={handleRowClick}
                  handleScrollClick={handleScrollClick}
                  maxDataLength={30}
                  tableData={filteredList}
                  rowsPerPage={200}
                  downloadButton={true}
                  header={t("deviceDetails.maintenanceTickets")}
                  specialDataFormatting={[
                    {
                      id: "attachments",
                      format: (data) => {
                        return data ? (
                          <Tooltip title={"This ticket has attached media"}>
                            <AttachFileIcon
                              color="primary"
                              style={{ fontSize: 20 }}
                            />
                          </Tooltip>
                        ) : (
                          ""
                        );
                      },
                    },
                    {
                      id: "expenses",
                      format: (data) => {
                        return data ? (
                          <Tooltip title={"This ticket has expenses"}>
                            <AttachMoneyIcon
                              color="primary"
                              style={{ fontSize: 20 }}
                            />
                          </Tooltip>
                        ) : (
                          ""
                        );
                      },
                    },
                    {
                      id: "state",
                      format: (data) => {
                        return t("ticketList." + data);
                      },
                    },
                    {
                      id: "priority",
                      format: (data) => {
                        return Object.keys(TICKET_PRIORITIES).find(
                          (f) => TICKET_PRIORITIES[f] === data,
                        );
                      },
                    },
                    {
                      id: "createdAt",
                      format: (data) => {
                        return formatTimeWithTZOffset(data, ECABIN);
                      },
                    },
                    {
                      id: "updatedAt",
                      format: (data) => {
                        return formatTimeWithTZOffset(data, ECABIN);
                      },
                    },
                  ]}
                />
              ) : (
                <CircularProgress />
              )}
            </Grid>
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  );
}
